/**
 * Formats a date string to yyyy-mm-dd hh:mm:ss format in the user's local timezone
 * @param dateString - ISO date string (e.g., "2025-08-14T13:36:59.852Z")
 * @returns formatted date string in local timezone
 */
export function formatDateToLocal(dateString: string): string {
  try {
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original string if there's an error
  }
}

/**
 * Formats a dd.mm.yyyy date string to a readable format like "August 28, 2025"
 * @param dateString - Date string in dd.mm.yyyy format (e.g., "28.08.2025")
 * @returns formatted date string in readable format
 */
export function formatDateFromDDMMYYYY(dateString: string): string {
  try {
    // Parse dd.mm.yyyy format
    const parts = dateString.split(".");
    if (parts.length !== 3) {
      return dateString; // Return original if format doesn't match
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date constructor
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return date.toLocaleDateString("en-US", options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original string if there's an error
  }
}

export function areDifferentDays(
  timestamp1: number,
  timestamp2: number
): boolean {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  // Compare dates in local timezone
  return (
    date1.getFullYear() !== date2.getFullYear() ||
    date1.getMonth() !== date2.getMonth() ||
    date1.getDate() !== date2.getDate()
  );
}

export function formatDateFromTimestamp(timestamp: number): string {
  const date = new Date(timestamp);

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return date.toLocaleDateString("en-US", options);
}
